import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from "react-router-dom";
import { allRemoteSettingsSelect, userSelect } from "../../redux/selectors";
import { capitalizeFirstLetter, notify } from "../../utility/Helper1";
import { authActions } from "../../redux/actions";
import { Images } from "../customComponents";
import ModernLoadingOverlay from '../customComponents/ModernLoadingOverlay';
import InvestmentCountdown from "../customComponents/InvestmentCountdown";
import BroadcastModal, { getUnreadCount } from "../BroadcastModal";

// MUI Components & Icons
import {
    Avatar,
    Box,
    Backdrop,
    CircularProgress,
    Typography,
    IconButton
} from '@mui/material';

import DashboardIcon from "@mui/icons-material/Dashboard";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonIcon from "@mui/icons-material/Person";
import WifiIcon from "@mui/icons-material/Wifi";
import BoltIcon from "@mui/icons-material/Bolt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LogoutIcon from "@mui/icons-material/Logout";
import CampaignIcon from "@mui/icons-material/Campaign";
import { Sun, Moon } from "lucide-react";


const saveScrollPosition = (path, scrollTop) => {
    try {
        sessionStorage.setItem(`scroll_${path}`, scrollTop);
    } catch (e) {}
};

const getScrollPosition = (path) => {
    try {
        return parseFloat(sessionStorage.getItem(`scroll_${path}`)) || 0;
    } catch (e) {
        return 0;
    }
};


function Section(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const hiddenFileInput = useRef(null);
    const mainRef = useRef(null);

    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);

    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [currentTime, setCurrentTime] = useState("");
    const [broadcastOpen, setBroadcastOpen] = useState(false);

    // Theme Management State
    const [theme, setTheme] = useState(() => {
        const storedTheme = localStorage.getItem("theme");
        if (storedTheme === "light" || storedTheme === "dark") {
            return storedTheme;
        }
        // Fallback to system preference
        const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        return systemPrefersDark ? "dark" : "light";
    });

    useEffect(() => {
        // Update document element data-theme attribute
        document.documentElement.setAttribute("data-theme", theme);
        // Persist to localStorage
        localStorage.setItem("theme", theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === "light" ? "dark" : "light");
    };

    // Reactive unread count — recalculates whenever news list changes
    const news = site_settings?.news || [];
    const [unreadCount, setUnreadCount] = useState(() => getUnreadCount(news));

    // Running clock logic
    useEffect(() => {
        const updateTime = () => {
            setCurrentTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
        };
        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, []);

    // Refresh unread count whenever news data changes (e.g. after Redux sync)
    useEffect(() => {
        setUnreadCount(getUnreadCount(news));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [news.length]);

    // Global Scroll Restoration Hook
    useEffect(() => {
        const mainEl = mainRef.current;
        if (!mainEl) return;

        const savedScroll = getScrollPosition(location.pathname);

        let attempts = 0;
        const maxAttempts = 15; // try for up to 1.5 seconds

        const restore = () => {
            if (!mainEl) return;
            mainEl.scrollTop = savedScroll;
            
            const currentScrollTop = mainEl.scrollTop;
            const canScroll = mainEl.scrollHeight > mainEl.clientHeight;

            // If we successfully restored the scroll, or if we have scrolled as far as possible (and there's a scrollbar)
            if (Math.abs(currentScrollTop - savedScroll) < 2 || (canScroll && currentScrollTop === mainEl.scrollHeight - mainEl.clientHeight)) {
                return;
            }

            if (attempts < maxAttempts) {
                attempts++;
                setTimeout(restore, 100);
            }
        };

        if (savedScroll > 0) {
            restore();
        } else {
            mainEl.scrollTop = 0;
        }

        const handleScroll = () => {
            if (mainEl) {
                saveScrollPosition(location.pathname, mainEl.scrollTop);
            }
        };

        mainEl.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            if (mainEl) {
                mainEl.removeEventListener("scroll", handleScroll);
            }
        };
    }, [location.pathname]);

    const progClickFileUpload = () => {
        hiddenFileInput.current?.click();
    };

    const uploadAvatar = async (file) => {
        if (!file) return;
        setUploadingAvatar(true);
        dispatch(authActions.onLogin("account/change-avatar", { avatar: file }, response => {
            setUploadingAvatar(false);
            if (response.code === 1) {
                notify("Avatar updated successfully", 1);
            } else {
                notify(response.msg || "Failed to update avatar", 0);
            }
        }));
    };

    const handleLogout = () => {
        dispatch(authActions.onLogout());
    };

    // Tabs mapping to the core router paths
    const TABS = [
        { id: "dashboard", label: "Dashboard", Icon: DashboardIcon, to: "/" },
        { id: "chat", label: "Chat", Icon: ChatBubbleIcon, to: "/chat" },
        { id: "transactions", label: "Transcript", Icon: DescriptionIcon, to: "/transactions" },
        { id: "referrals", label: "Referrals", Icon: GroupAddIcon, to: "/affiliate-center" },
        { id: "profile", label: "Profile", Icon: PersonIcon, to: "/personal-information" },
    ];

    // Helper to determine if we are on one of the main 5 tabs
    const currentPath = location.pathname;
    const activeTab = TABS.find(tab => {
        if (tab.to === "/") {
            return currentPath === "/";
        }
        return currentPath.startsWith(tab.to);
    });

    const isMainTab = !!activeTab;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
                * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
                body { 
                    background-color: var(--body-bg) !important; 
                    margin: 0 !important; 
                    padding: 0 !important;
                    font-family: 'DM Sans', sans-serif !important;
                    color: var(--text-main) !important;
                }
                .no-scrollbar::-webkit-scrollbar { display: none !important; }
                .no-scrollbar { -ms-overflow-style: none !important; scrollbar-width: none !important; }
                .device-frame-container {
                    width: 100%;
                    max-width: 100%;
                    height: 100vh;
                    height: 100dvh;
                    max-height: 100vh;
                    max-height: 100dvh;
                    background: var(--app-bg);
                    font-family: 'DM Sans', sans-serif;
                    overflow: hidden;
                    position: relative;
                    border: none;
                    box-shadow: none;
                }
                .main-content-scrollport {
                    max-height: calc(100vh - 128px) !important;
                    max-height: calc(100dvh - 128px) !important;
                }
                @media (max-width: 767px) {
                    .mobile-canvas-override {
                        height: 100vh !important;
                        height: 100dvh !important;
                        min-height: 100vh !important;
                        min-height: 100dvh !important;
                        max-height: 100vh !important;
                        max-height: 100dvh !important;
                        overflow: hidden !important;
                        position: fixed !important;
                        top: 0 !important;
                        left: 0 !important;
                        width: 100vw !important;
                    }
                }
                @media (min-width: 768px) {
                    .device-frame-container {
                        max-width: 420px;
                        border-radius: 24px;
                        box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.2);
                    }
                }
            `}</style>

            {/* Desktop Screen Canvas */}
            <div className="mobile-canvas-override min-h-screen flex items-center justify-center p-0 md:py-6" style={{ background: "var(--desktop-canvas-bg)" }}>

                {/* Simulated Phone Device Frame */}
                <div className="relative flex flex-col device-frame-container">

                    {/* Ambient Glow Elements inside phone simulator */}
                    <div className="pointer-events-none absolute inset-0" style={{
                        backgroundImage: "radial-gradient(ellipse at 50% -10%, rgba(59, 130, 246, 0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(99, 102, 241, 0.04) 0%, transparent 50%)",
                        zIndex: 0,
                    }} />

                    {/* Unified Mobile Header Bar */}
                    <header className="flex items-center justify-between px-4 py-3 shrink-0 relative z-20" style={{
                        background: "var(--bar-bg)",
                        backdropFilter: "blur(12px)",
                        borderBottom: "1px solid var(--border-color)",
                        height: 58
                    }}>
                        {isMainTab ? (
                            // Left Brand elements on main tab
                            <div className="flex items-center gap-2">
                                {site_settings?.site_settings?.logo && (
                                    <Images height={24} style={{ height: 24, maxHeight: 24, width: "auto", display: "block" }} source={site_settings?.site_settings?.logo} alt="logo" />
                                )}
                                {/* <span style={{
                                    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    fontSize: 16,
                                    fontWeight: 800,
                                    fontFamily: "'Syne', sans-serif",
                                    letterSpacing: "-0.02em"
                                }}>
                                    {capitalizeFirstLetter(site_settings?.site_settings?.sitename || "")}
                                </span> */}
                            </div>
                        ) : (
                            // Left Back Button on inner/nested routes
                            <button
                                onClick={() => navigate(-1)}
                                className="rounded-xl flex items-center justify-center transition-all active:scale-90"
                                style={{
                                    width: 36,
                                    height: 36,
                                    background: "var(--border-color)",
                                    border: "1px solid var(--border-color)",
                                    outline: "none"
                                }}
                            >
                                <ChevronLeftIcon style={{ color: "#2563eb", fontSize: 20 }} />
                            </button>
                        )}

                        {/* Center Title or operational countdown */}
                        <div className="flex-1 text-center px-2">
                            {(isMainTab) ? (
                                // Render countdown when on the main Dashboard tab
                                activeTab?.id === "dashboard" && site_settings?.operational_settings && (
                                    <div className="inline-flex justify-center scale-90">
                                        {/* <InvestmentCountdown operationalSettings={site_settings?.operational_settings} /> */}
                                    </div>

                                )
                            ) : (
                                // Render sub-page title
                                <span style={{
                                    color: "var(--text-main)",
                                    fontSize: 14,
                                    fontWeight: 700,
                                    fontFamily: "'Syne', sans-serif"
                                }}>
                                    {props.title || "Details"}
                                </span>
                            )}
                        </div>

                        {/* Right elements: Theme toggle, Logout and Avatar */}
                        <div className="flex items-center gap-2">
                            {isMainTab ? (
                                <>
                                    {/* Beautiful Premium Sliding Theme Toggle */}
                                    <div 
                                        onClick={toggleTheme}
                                        className={`theme-toggle-pill ${theme}`}
                                        title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                                    >
                                        <div className="theme-toggle-icons">
                                            <span className="theme-toggle-icon sun"><Sun size={10} color="#fbbf24" fill="#fbbf24" /></span>
                                            <span className="theme-toggle-icon moon"><Moon size={10} color="#818cf8" fill="#818cf8" /></span>
                                        </div>
                                        <div className="theme-toggle-thumb">
                                            {theme === 'dark' ? (
                                                <Moon size={12} color="#ffffff" fill="#ffffff" />
                                            ) : (
                                                <Sun size={12} color="#ffffff" fill="#ffffff" className="spin-slow" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Broadcast hanger icon with unread badge */}
                                    <div style={{ position: "relative" }}>
                                        <IconButton
                                            onClick={() => {
                                                setBroadcastOpen(true);
                                            }}
                                            sx={{
                                                p: 0.5,
                                                background: unreadCount > 0
                                                    ? "rgba(37, 99, 235, 0.1)"
                                                    : "rgba(59, 130, 246, 0.06)",
                                                border: unreadCount > 0
                                                    ? "1px solid rgba(37, 99, 235, 0.25)"
                                                    : "1px solid rgba(59, 130, 246, 0.12)",
                                                borderRadius: "10px",
                                                transition: "all 0.2s",
                                            }}
                                        >
                                            <CampaignIcon style={{ color: unreadCount > 0 ? "#2563eb" : "#64748b", fontSize: 18 }} />
                                        </IconButton>
                                        {unreadCount > 0 && (
                                            <div style={{
                                                position: "absolute",
                                                top: -4,
                                                right: -4,
                                                minWidth: 16,
                                                height: 16,
                                                borderRadius: 8,
                                                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                                color: "#fff",
                                                fontSize: 9,
                                                fontWeight: 800,
                                                fontFamily: "'DM Sans', sans-serif",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: "0 3px",
                                                border: "1.5px solid #fff",
                                                boxShadow: "0 2px 6px rgba(239,68,68,0.4)",
                                                pointerEvents: "none",
                                            }}>
                                                {unreadCount > 9 ? "9+" : unreadCount}
                                            </div>
                                        )}
                                    </div>

                                    {/* Sleek Logout button */}
                                    <IconButton
                                        onClick={handleLogout}
                                        sx={{
                                            p: 0.5,
                                            background: "rgba(239, 68, 68, 0.06)",
                                            border: "1px solid rgba(239, 68, 68, 0.1)",
                                            borderRadius: "10px"
                                        }}
                                    >
                                        <LogoutIcon style={{ color: "#ef4444", fontSize: 16 }} />
                                    </IconButton>

                                    {/* Profile Avatar Triggering File Uploader */}
                                    <div className="relative">
                                        {uploadingAvatar && (
                                            <Backdrop open sx={{ position: 'absolute', zIndex: 5, borderRadius: '50%' }}>
                                                <CircularProgress size={16} sx={{ color: '#3b82f6' }} />
                                            </Backdrop>
                                        )}
                                        <Avatar
                                            onClick={progClickFileUpload}
                                            src={userData?.image || undefined}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                cursor: 'pointer',
                                                border: '2px solid #ffffff',
                                                boxShadow: '0 4px 10px rgba(59, 130, 246, 0.15)',
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                fontSize: '0.8rem',
                                                fontWeight: 'black',
                                                color: '#fff',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                }
                                            }}
                                        >
                                            {!userData?.image && userData?.username?.charAt(0)?.toUpperCase()}
                                        </Avatar>
                                        <input
                                            ref={hiddenFileInput}
                                            type="file"
                                            accept=".jpg,.gif,.png,.jpeg"
                                            onChange={(e) => uploadAvatar(e.target.files[0])}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div 
                                    onClick={toggleTheme}
                                    className={`theme-toggle-pill ${theme}`}
                                    title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                                >
                                    <div className="theme-toggle-icons">
                                        <span className="theme-toggle-icon sun"><Sun size={10} color="#fbbf24" fill="#fbbf24" /></span>
                                        <span className="theme-toggle-icon moon"><Moon size={10} color="#818cf8" fill="#818cf8" /></span>
                                    </div>
                                    <div className="theme-toggle-thumb">
                                        {theme === 'dark' ? (
                                            <Moon size={12} color="#ffffff" fill="#ffffff" />
                                        ) : (
                                            <Sun size={12} color="#ffffff" fill="#ffffff" className="spin-slow" />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </header>

                    {/* Main Child View Area (Scrollport) */}
                    <main ref={mainRef} className={`flex-1 no-scrollbar relative z-10 main-content-scrollport ${props.unpadded ? "p-0" : "p-4"}`} style={{
                        overflowY: props.noScroll ? "hidden" : "auto",
                        display: props.noScroll ? "flex" : "block",
                        flexDirection: props.noScroll ? "column" : "initial"
                    }}>
                        {props.children}
                    </main>


                    {/* Bottom Navigation matching Home2.js exactly */}
                    <nav className="shrink-0 relative z-20 flex items-center justify-around px-2 py-2" style={{
                        background: "var(--bar-bg)",
                        borderTop: "1px solid var(--border-color)",
                        backdropFilter: "blur(16px)",
                        boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.03)",
                        paddingBottom: "max(env(safe-area-inset-bottom), 10px)",
                        height: 70
                    }}>
                        {TABS.map(({ id, label, Icon, to }) => {
                            const active = activeTab?.id === id;
                            return (
                                <button
                                    key={id}
                                    onClick={() => navigate(to)}
                                    className="flex flex-col items-center gap-1 flex-1 py-1 transition-all active:scale-90"
                                    style={{ outline: "none", border: "none", background: "transparent", cursor: "pointer" }}
                                >
                                    <div className="relative flex items-center justify-center rounded-xl transition-all" style={{
                                        width: 44,
                                        height: 32,
                                        background: active ? "var(--border-color)" : "transparent",
                                        boxShadow: active ? "0 0 12px rgba(59, 130, 246, 0.1)" : "none",
                                        transition: "all 0.2s",
                                    }}>
                                        <Icon style={{ fontSize: 18, color: active ? "#2563eb" : "var(--text-muted)", transition: "color 0.2s" }} />

                                        {id === "chat" && 1 == 0 && (
                                            <div className="absolute -top-1 -right-1 rounded-full text-white flex items-center justify-center" style={{ width: 14, height: 14, background: "#3b82f6", fontSize: 8, fontWeight: 800, fontFamily: "'Syne',sans-serif" }}>3</div>
                                        )}

                                        {active && (
                                            <div className="absolute -bottom-1 rounded-full" style={{
                                                width: 4,
                                                height: 4,
                                                background: "#3b82f6",
                                                boxShadow: "0 0 8px #3b82f6"
                                            }} />
                                        )}
                                    </div>
    <span style={{
        fontSize: 9,
        color: active ? "#2563eb" : "var(--text-muted)",
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: active ? 600 : 400,
        transition: "color 0.2s"
    }}>
        {label}
    </span>
                                </button>
                            );
                        })}
                    </nav>

                    {/* Standard Modern Loading Overlay */}
                    <ModernLoadingOverlay
                        open={props.loading || false}
                        text={props.overlayText || 'Processing your request...'}
                        showProgressDots={true}
                        size={60}
                        thickness={4}
                    />

                    {/* Broadcast Modal — mounts globally above all content */}
                    <BroadcastModal
                        open={broadcastOpen}
                        onClose={() => {
                            setBroadcastOpen(false);
                            // Refresh badge count after user reads broadcasts
                            setUnreadCount(getUnreadCount(news));
                        }}
                    />

                </div>

            </div>
        </>
    );
}

export default Section;